import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Sparkles, RefreshCw } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import { formatDateLong } from '@/lib/format';
import { toast } from 'sonner';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';

export const Route = createFileRoute('/insights')({
  component: InsightsPage,
});

function InsightsPage() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['insights'], queryFn: () => api.listInsights() });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const generateMut = useMutation({
    mutationFn: (type: 'weekly_bilan' | 'monthly_bilan') => api.generateBilan(type),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['insights'] });
      toast.success('Bilan généré');
    },
  });

  const selected = data?.find((d) => d.id === selectedId) ?? data?.[0] ?? null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Bilans IA</h1>
          <p className="text-sm text-muted-foreground">Bilans hebdo / mensuel + analyses des objectifs</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => generateMut.mutate('weekly_bilan')} disabled={generateMut.isPending}>
            <RefreshCw className="h-4 w-4" /> Générer hebdo
          </Button>
          <Button onClick={() => generateMut.mutate('monthly_bilan')} disabled={generateMut.isPending}>
            <Sparkles className="h-4 w-4" /> Générer mensuel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[280px_1fr]">
        <Card>
          <CardHeader>
            <CardTitle>Historique</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {(data ?? []).map((i) => (
              <button
                key={i.id}
                onClick={() => setSelectedId(i.id)}
                className={`w-full rounded-md p-2 text-left text-sm transition-colors ${
                  (selected?.id === i.id) ? 'bg-muted' : 'hover:bg-muted/50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{labelForType(i.type)}</span>
                  <Badge variant="outline" className="text-[10px]">{i.modelUsed.split('-')[1] ?? 'IA'}</Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatDateLong(i.periodStart)}
                </div>
              </button>
            ))}
            {(!data || data.length === 0) && (
              <div className="text-sm text-muted-foreground p-2">Aucun bilan généré</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none p-6">
            {selected ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{selected.contentMd}</ReactMarkdown>
            ) : (
              <p className="text-muted-foreground">Sélectionne un bilan pour le lire ou génère le premier.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function labelForType(t: string): string {
  switch (t) {
    case 'weekly_bilan':
      return 'Bilan hebdo';
    case 'monthly_bilan':
      return 'Bilan mensuel';
    case 'goal_review':
      return 'Coach objectif';
    case 'recommendation':
      return 'Recommandation';
    default:
      return t;
  }
}
